import { useCallback, useEffect, useState } from 'react';

import { api } from '@api/reduxApi';
import { getTrackProps } from '@components/GQSurveyBuilder/utils';
import { track } from '@components/tracking';
import { useToaster } from 'components/stores/toaster';

export const useScreenerConfig = (study: Study, screener: Screener) => {
  const showToast = useToaster();
  const [updateStudy, { isLoading }] = api.useUpdateStudyMutation();
  const [disqualificationScreenerReview, setDisqualificationScreenerReview] = useState<
    Study['disqualification_screener_review']
  >(study.disqualification_screener_review || 'manual');
  const [qualificationScreenerReview, setQualificationScreenerReview] = useState<
    Study['qualification_screener_review']
  >(study.qualification_screener_review || 'manual');
  const [disqualificationPageTitle, setDisqualificationPageTitle] = useState<string>(
    study.disqualification_page_title || 'Thank you for your interest.'
  );
  const [disqualificationPageContent, setDisqualificationPageContent] = useState<string>(
    study.disqualification_page_content ||
      "Unfortunately, you don't meet the criteria for this study at this time. We appreciate your time and interest in participating."
  );
  const [disqualificationPageTitleError, setDisqualificationPageTitleError] = useState<boolean | null>(null);
  const [disqualificationPageContentError, setDisqualificationPageContentError] = useState<boolean | null>(null);

  // Reset values and clear errors when switching to manual mode
  useEffect(() => {
    if (disqualificationScreenerReview === 'manual') {
      setDisqualificationPageTitle(study.disqualification_page_title || 'Thank you for your interest.');
      setDisqualificationPageContent(
        study.disqualification_page_content ||
          "Unfortunately, you don't meet the criteria for this study at this time. We appreciate your time and interest in participating."
      );
      setDisqualificationPageTitleError(false);
      setDisqualificationPageContentError(false);
    }
  }, [disqualificationScreenerReview]);

  const resetDisqualificationPageToInitialValues = useCallback(() => {
    setDisqualificationPageTitle(study.disqualification_page_title || 'Thank you for your interest.');
    setDisqualificationPageContent(
      study.disqualification_page_content ||
        "Unfortunately, you don't meet the criteria for this study at this time. We appreciate your time and interest in participating."
    );
  }, [study.disqualification_page_title, study.disqualification_page_content]);

  const handleSave = useCallback(async (): Promise<boolean> => {
    track('saved_question', getTrackProps(screener));

    if (disqualificationScreenerReview === 'auto') {
      const titleIsEmpty = !disqualificationPageTitle;
      const contentIsEmpty = !disqualificationPageContent;

      setDisqualificationPageTitleError(titleIsEmpty);
      setDisqualificationPageContentError(contentIsEmpty);

      if (titleIsEmpty || contentIsEmpty) {
        return false;
      }
    }

    try {
      await updateStudy({
        id: study.id,
        qualification_screener_review: qualificationScreenerReview,
        disqualification_screener_review: disqualificationScreenerReview,
        ...(disqualificationScreenerReview === 'auto' && {
          disqualification_page_title: disqualificationPageTitle,
          disqualification_page_content: disqualificationPageContent
        })
      }).unwrap();

      setDisqualificationPageTitleError(false);
      setDisqualificationPageContentError(false);
      return true;
    } catch (error: any) {
      const errorMessage = Array.isArray(error.data?.errors?.['fields.base'])
        ? error.data.errors['fields.base'].join(',')
        : 'Something went wrong. Please try again later.';

      showToast({
        heading: 'Failed to update!',
        text: errorMessage,
        icon: 'error'
      });
      return false;
    }
  }, [
    screener,
    study,
    showToast,
    updateStudy,
    qualificationScreenerReview,
    disqualificationScreenerReview,
    disqualificationPageTitle,
    disqualificationPageContent
  ]);

  return {
    isLoading,
    handleSave,
    setDisqualificationScreenerReview,
    disqualificationScreenerReview,
    qualificationScreenerReview,
    setQualificationScreenerReview,
    disqualificationPageTitle,
    setDisqualificationPageTitle,
    disqualificationPageContent,
    setDisqualificationPageContent,
    disqualificationPageTitleError,
    disqualificationPageContentError,
    resetDisqualificationPageToInitialValues
  };
};
